import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ArrowRight, Bot, Code, BarChart, Sun, Moon, Briefcase, 
  TrendingDown, Flame, Shuffle, Hourglass, Target, Smartphone, 
  Users, Shield, Palette, Check, Star, ChevronDown, Menu, X,Play,Clock
} from 'lucide-react';

const customStyles = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }
  .animate-float { animation: float 4s ease-in-out infinite; }
`;

// --- REUSABLE COMPONENTS ---

const FAQS = [
  { question: "How long does it take to set up for a new role?", answer: "Under 10 minutes. Paste your JD, the AI generates a structured interview, you review and publish. Send the invite link — your first candidates can be screened the same hour." },
  { question: "Can it handle coding and technical assessments?", answer: "Yes! RecruitGuru includes a built-in Monaco code editor and compiler. The AI can watch candidates write React, Python, Java, or C++ in real-time, evaluating their logic, syntax, and spoken explanations simultaneously." },
  { question: "Will candidates drop off without a human interviewer?", answer: "Completion rates are typically 60–75% — often higher than a scheduled phone screen. Candidates complete it on their own time, which reduces anxiety and no-shows significantly." },
  { question: "Can I brand it with my agency's logo and name?", answer: "Yes! On the Enterprise plan, every candidate-facing touchpoint carries your brand. Interview portal, emails, report cards, and reminders all show your agency identity." },
  { question: "How does the anti-cheat system work?", answer: "We monitor tab switches, screen-sharing drops, and use AI to verify consistency in voice and responses. If a candidate leaves the interview screen multiple times, their session is automatically terminated and flagged on your dashboard." },
];

const FaqItem = ({ question, answer, isOpen, onToggle }) => {
  return (
    <div className="border-b border-slate-200 dark:border-white/10">
      <button
        onClick={onToggle}
        className="w-full py-6 flex justify-between items-center text-left text-slate-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
      >
        <span className="text-base font-medium">{question}</span>
        <div className={`w-8 h-8 rounded-lg bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 flex items-center justify-center shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 bg-indigo-50 dark:bg-indigo-500/10 border-indigo-200 dark:border-indigo-500/30 text-indigo-600 dark:text-indigo-400' : 'text-slate-400'}`}>
          <ChevronDown size={16} />
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-48 pb-6 opacity-100' : 'max-h-0 opacity-0'}`}>
        <p className="text-[15px] font-normal text-slate-600 dark:text-slate-400 leading-relaxed pr-8">{answer}</p>
      </div>
    </div>
  );
};

export const PublicNav = ({ isDark, setIsDark }) => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 w-full z-50 backdrop-blur-md border-b border-slate-200/50 dark:border-white/5 bg-white/50 dark:bg-[#0a0a0a]/50">
      <div className="px-6 lg:px-12 py-4 md:py-6 flex items-center justify-between">
        
        {/* Brand Logo */}
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
          <div className="w-8 h-8 flex items-center justify-center border border-slate-900 dark:border-white rounded-full bg-indigo-600 dark:bg-transparent shadow-[0_0_15px_rgba(99,102,241,0.5)] dark:shadow-none">
            <Briefcase size={14} className="text-white dark:text-white" strokeWidth={2} />
          </div>
          <span className="font-bold text-lg tracking-[0.1em] text-slate-900 dark:text-white">RecruitGuru</span>
        </div>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-6">
          <nav className="flex items-center gap-6 text-xs font-bold tracking-wide text-slate-500 dark:text-slate-400">
            <a href="/#how" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">How it Works</a>
            <a href="/#features" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Features</a>
            <a href="/#pricing" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Pricing</a>
            <Link to="/contact" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Contact</Link>
          </nav>
          
          <button onClick={() => setIsDark(!isDark)} className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors duration-300 focus:outline-none ${isDark ? 'bg-indigo-500/20 border border-indigo-500/30' : 'bg-slate-200 border border-slate-300 shadow-sm'}`}>
            <span className={`inline-flex h-4 w-4 transform items-center justify-center rounded-full transition-transform duration-300 ${isDark ? 'translate-x-7 bg-indigo-400' : 'translate-x-1 bg-white shadow-sm'}`}>
              {isDark ? <Moon size={10} className="text-[#0a0a0a]" /> : <Sun size={10} className="text-slate-400" />}
            </span>
          </button>

          <Link to="/login" className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-[12px] font-bold tracking-wide transition-colors shadow-[0_0_15px_rgba(99,102,241,0.3)]">
            Login
          </Link>
        </div>

        {/* Mobile Controls (Theme + Hamburger) */}
        <div className="flex md:hidden items-center gap-4">
          <button onClick={() => setIsDark(!isDark)} className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors duration-300 focus:outline-none ${isDark ? 'bg-indigo-500/20 border border-indigo-500/30' : 'bg-slate-200 border border-slate-300 shadow-sm'}`}>
            <span className={`inline-flex h-4 w-4 transform items-center justify-center rounded-full transition-transform duration-300 ${isDark ? 'translate-x-7 bg-indigo-400' : 'translate-x-1 bg-white shadow-sm'}`}>
              {isDark ? <Moon size={10} className="text-[#0a0a0a]" /> : <Sun size={10} className="text-slate-400" />}
            </span>
          </button>

          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
            className="text-slate-900 dark:text-white p-1"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Dropdown Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-white dark:bg-[#0a0a0a] border-b border-slate-200 dark:border-white/10 shadow-2xl flex flex-col py-4 px-6 gap-6 animate-in slide-in-from-top-2">
          <a href="/#how" onClick={() => setIsMobileMenuOpen(false)} className="text-xs font-bold tracking-wide text-slate-600 dark:text-slate-300">How it Works</a>
          <a href="/#features" onClick={() => setIsMobileMenuOpen(false)} className="text-xs font-bold tracking-wide text-slate-600 dark:text-slate-300">Features</a>
          <a href="/#pricing" onClick={() => setIsMobileMenuOpen(false)} className="text-xs font-bold tracking-wide text-slate-600 dark:text-slate-300">Pricing</a>
          <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="text-xs font-bold tracking-wide text-slate-600 dark:text-slate-300">Contact Sales</Link>
          
          <div className="h-px w-full bg-slate-200 dark:bg-white/10 my-2"></div>
          
          <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="w-full text-center px-5 py-3 bg-indigo-600 text-white rounded-xl text-xs font-bold tracking-wide">
            Sign In to Dashboard
          </Link>
        </div>
      )}
    </header>
  );
};
// --- MAIN PAGE ---

const Landing = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [isDark, setIsDark] = useState(() => {
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme) return storedTheme === 'dark';
    if (document.documentElement.classList.contains('dark')) return true;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    if (isDark) { document.documentElement.classList.add('dark'); localStorage.setItem('theme', 'dark'); } 
    else { document.documentElement.classList.remove('dark'); localStorage.setItem('theme', 'light'); }
  }, [isDark]);

  return (
    <div className="min-h-[100dvh] bg-slate-50 dark:bg-[#05050A] text-slate-900 dark:text-slate-200 font-sans relative overflow-hidden transition-colors duration-500 flex flex-col scroll-smooth">
      <style>{customStyles}</style>

      {/* Global Background Blobs */}
      <div className="fixed top-[-10%] left-[10%] w-[50vw] h-[50vh] rounded-full bg-indigo-600/10 blur-[120px] animate-blob z-0 pointer-events-none"></div>
      <div className="fixed bottom-[10%] right-[10%] w-[50vw] h-[50vh] rounded-full bg-violet-600/10 blur-[120px] animate-blob z-0 pointer-events-none" style={{ animationDelay: '2s' }}></div>

      <PublicNav isDark={isDark} setIsDark={setIsDark} />

      {/* 1. HERO SECTION */}
      <section className="pt-40 pb-20 px-6 relative z-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 text-indigo-600 dark:text-indigo-400 text-[12px] font-bold tracking-wide mb-6 animate-in fade-in slide-in-from-bottom-4">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse"></span>
              Built for Staffing & Recruitment Agencies
            </div>
            <h1 className="text-5xl lg:text-7xl font-bold tracking-tight text-slate-900 dark:text-white mb-6 leading-[1.1] animate-in fade-in slide-in-from-bottom-4">
              Screen 100 candidates<br/>before <span className="font-light italic bg-gradient-to-r from-indigo-500 to-violet-500 bg-clip-text text-transparent">lunch.</span>
            </h1>
            <p className="text-lg font-light text-slate-600 dark:text-slate-400 mb-8 leading-relaxed max-w-lg animate-in fade-in slide-in-from-bottom-4" style={{ animationDelay: '100ms' }}>
              RecruitGuru replaces first-round interviews with an intelligent agent. Spend time placing candidates, not screening them. Scored report cards delivered in minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-in fade-in slide-in-from-bottom-4" style={{ animationDelay: '200ms' }}>
              <button onClick={() => navigate('/register')} className="px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold tracking-wide transition-all shadow-[0_0_20px_rgba(99,102,241,0.4)] active:scale-95 flex items-center justify-center gap-2">
                Start Free Trial <ArrowRight size={16} />
              </button>
              <button onClick={() => navigate('/candidate-login')} className="px-8 py-4 bg-white dark:bg-[#111] border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-xl text-xs font-bold tracking-wide transition-all shadow-sm flex items-center justify-center gap-2">
                <Play size={14} /> Watch 2-min demo
              </button>
            </div>
            <div className="mt-8 flex items-center gap-6 text-[12px] font-bold text-slate-500 tracking-wide animate-in fade-in slide-in-from-bottom-4" style={{ animationDelay: '300ms' }}>
              <span className="flex items-center gap-1.5"><Check size={14} className="text-indigo-500"/> 14-day free trial</span>
              <span className="flex items-center gap-1.5"><Check size={14} className="text-indigo-500"/> No credit card</span>
            </div>
          </div>
          
          {/* Mock Dashboard UI */}
          <div className="relative animate-in fade-in zoom-in-95 duration-700" style={{ animationDelay: '200ms' }}>
            <div className="bg-white dark:bg-[#0a0a0a] border border-slate-200 dark:border-white/10 rounded-[2rem] shadow-2xl overflow-hidden backdrop-blur-xl">
              <div className="bg-slate-100 dark:bg-white/5 border-b border-slate-200 dark:border-white/10 px-4 py-3 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-rose-500"></div>
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                <span className="text-[12px] font-bold text-slate-400 tracking-wide mx-auto">RecruitGuru Pipeline</span>
              </div>
              <div className="p-6">
                <div className="flex justify-between items-end mb-6">
                  <div>
                    <div className="text-[12px] font-bold tracking-wide text-slate-500 mb-1">Senior React Developer</div>
                    <div className="text-2xl font-light text-slate-900 dark:text-white">47 Screened</div>
                  </div>
                  <div className="px-3 py-1 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-[12px] font-bold tracking-wide rounded-lg">
                    12 Shortlisted
                  </div>
                </div>
                
                <div className="space-y-3">
                  {[
                    { initials: 'PR', name: 'Priya Rajesh', exp: '8 yrs · IT Staffing', score: 92, color: 'emerald' },
                    { initials: 'AK', name: 'Arjun Kulkarni', exp: '5 yrs · BFSI', score: 87, color: 'emerald' },
                    { initials: 'SM', name: 'Sneha Mehta', exp: '3 yrs · Healthcare', score: 71, color: 'amber' },
                    { initials: 'RV', name: 'Rahul Verma', exp: '2 yrs · General', score: 44, color: 'rose' }
                  ].map((c, i) => (
                    <div key={i} className="flex items-center gap-4 p-3 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/5">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold bg-${c.color}-100 dark:bg-${c.color}-500/20 text-${c.color}-600 dark:text-${c.color}-400`}>
                        {c.initials}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-bold text-slate-900 dark:text-white">{c.name}</div>
                        <div className="text-[12px] text-slate-500">{c.exp}</div>
                      </div>
                      <div className={`px-2 py-1 rounded-md text-xs font-bold bg-${c.color}-50 dark:bg-${c.color}-500/10 text-${c.color}-600 dark:text-${c.color}-400 border border-${c.color}-200 dark:border-${c.color}-500/20`}>
                        {c.score}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Floating Badge */}
            <div className="absolute -bottom-6 -left-6 bg-white dark:bg-[#111] border border-slate-200 dark:border-white/10 p-4 rounded-2xl shadow-xl flex items-center gap-4 animate-float">
              <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                <Clock size={24} />
              </div>
              <div>
                <div className="text-xl font-bold text-indigo-600 dark:text-indigo-400">80%</div>
                <div className="text-[12px] font-bold text-slate-500 tracking-wide">Less screening time</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. TRUST BAR */}
      <section className="py-12 border-y border-slate-200/50 dark:border-white/5 bg-white/50 dark:bg-black/20 backdrop-blur-md relative z-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center gap-8 justify-center opacity-70 grayscale dark:grayscale-0 dark:opacity-50">
          <span className="text-[12px] font-bold tracking-wide text-slate-500 text-center md:text-left">Trusted by top agencies</span>
          <div className="hidden md:block w-px h-8 bg-slate-300 dark:bg-white/20"></div>
          <div className="flex flex-wrap justify-center gap-8 md:gap-12 font-bold text-lg tracking-wide text-slate-700 dark:text-white">
            <span>TalentBridge</span>
            <span>HireQuick</span>
            <span>PeoplePath</span>
            <span>CoreHire</span>
          </div>
        </div>
      </section>

      {/* 3. PAIN POINTS */}
      <section className="py-24 px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <span className="text-[12px] font-bold tracking-wide text-indigo-600 dark:text-indigo-400 flex items-center gap-2 mb-3"><div className="w-4 h-0.5 bg-indigo-600 dark:bg-indigo-400"></div> THE PROBLEM</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6 tracking-tight">Your recruiters waste 5 hours a day<br/>on <span className="font-light italic text-indigo-600 dark:text-indigo-400">dead-end screens.</span></h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 font-light max-w-2xl">Staffing agencies lose more margin to manual screening than anything else. Here is what is actually happening.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: <TrendingDown size={28}/>, title: "Manual screening kills your margin", desc: "A recruiter spends 5-6 hours on first-round screens that go nowhere. Every hour screening is an hour not placing." },
              { icon: <Flame size={28}/>, title: "High-volume roles burn out your team", desc: "When a client needs 50 BPO agents in a week, manual interviewing doesn't scale. You miss the deadline or burn your recruiters." },
              { icon: <Shuffle size={28}/>, title: "Inconsistent shortlisting costs clients", desc: "Different recruiters ask different questions and score differently. One bad shortlist and your client relationship is at risk." },
              { icon: <Hourglass size={28}/>, title: "Candidates ghost before you even talk", desc: "Scheduling a call takes 2-3 days. By then, your best candidates have moved on. AI links are completed on their own time." }
            ].map((pain, i) => (
              <div key={i} className="p-10 rounded-[2rem] bg-white/60 dark:bg-[#0a0a0a]/60 backdrop-blur-md border border-slate-200 dark:border-white/10 shadow-sm relative overflow-hidden group hover:border-indigo-500/50 transition-colors">
                <div className="absolute -top-4 -right-4 text-[8rem] font-bold text-slate-100 dark:text-white/5 leading-none pointer-events-none group-hover:scale-110 transition-transform">0{i+1}</div>
                <div className="text-indigo-600 dark:text-indigo-400 mb-6">{pain.icon}</div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3 relative z-10">{pain.title}</h3>
                <p className="text-[15px] font-normal text-slate-600 dark:text-slate-400 leading-relaxed relative z-10">{pain.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. HOW IT WORKS */}
      <section id="how" className="py-24 px-6 bg-slate-100/50 dark:bg-[#0a0a0a]/30 relative z-10 border-y border-slate-200/50 dark:border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 text-center flex flex-col items-center">
            <span className="text-[12px] font-bold tracking-wide text-indigo-600 dark:text-indigo-400 flex items-center gap-2 mb-3"><div className="w-4 h-0.5 bg-indigo-600 dark:bg-indigo-400"></div> HOW IT WORKS</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6 tracking-tight">From job brief to shortlist<br/>in <span className="font-light italic text-indigo-600 dark:text-indigo-400">hours, not days.</span></h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 font-light max-w-2xl">Four steps. No training needed. Your first candidates screened the same day you sign up.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-6 relative">
            <div className="hidden md:block absolute top-[36px] left-[12%] right-[12%] h-px bg-gradient-to-r from-indigo-200 via-indigo-500 to-indigo-200 dark:from-white/5 dark:via-indigo-500 dark:to-white/5"></div>
            {[
              { label: "Create", title: "Set up your role", desc: "Paste your JD or describe the role. AI builds a structured, adaptive interview in under 10 minutes." },
              { label: "Invite", title: "Send links", desc: "Share via Email, WhatsApp, or Bulk CSV. Automated reminders chase candidates so you don't have to." },
              { label: "Screen", title: "AI does the talking", desc: "Adaptive voice or text interviews that probe weak answers just like a real human recruiter." },
              { label: "Shortlist", title: "Get the report card", desc: "Every candidate scored on skills, communication, and red flags. Review 50 candidates in 20 minutes." }
            ].map((step, i) => (
              <div key={i} className="relative z-10 flex flex-col items-center text-center">
                <div className="w-[72px] h-[72px] rounded-2xl bg-white dark:bg-[#111] border border-slate-200 dark:border-white/10 flex items-center justify-center text-2xl font-bold text-indigo-600 dark:text-indigo-400 shadow-xl mb-6">
                  {i+1}
                </div>
                <div className="text-[12px] font-bold tracking-wide text-indigo-600 dark:text-indigo-400 mb-2">{step.label}</div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-3">{step.title}</h3>
                <p className="text-[15px] font-normal text-slate-600 dark:text-slate-400 leading-relaxed px-4">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. FEATURES */}
      <section id="features" className="py-24 px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <span className="text-[12px] font-bold tracking-wide text-indigo-600 dark:text-indigo-400 flex items-center gap-2 mb-3"><div className="w-4 h-0.5 bg-indigo-600 dark:bg-indigo-400"></div> CORE FEATURES</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6 tracking-tight">Built for agencies.<br/>Not <span className="font-light italic text-indigo-600 dark:text-indigo-400">generic HR.</span></h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 font-light max-w-2xl">Every feature is designed around the way staffing and recruitment agencies actually work.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 p-10 rounded-[2rem] bg-gradient-to-br from-indigo-50 to-white dark:from-indigo-900/20 dark:to-[#0a0a0a]/60 backdrop-blur-md border border-indigo-100 dark:border-indigo-500/20 shadow-sm relative overflow-hidden group">
              <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 rounded-xl flex items-center justify-center mb-6"><Target size={24}/></div>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Adaptive AI Interviewing</h3>
              <p className="text-base font-light text-slate-600 dark:text-slate-400 leading-relaxed max-w-xl">Our AI adjusts questions based on each candidate's responses — probing deeper on vague answers, moving faster with strong candidates. It feels like a seasoned recruiter, not a rigid form.</p>
              <div className="mt-6 text-[12px] font-bold tracking-wide text-indigo-600 dark:text-indigo-400 flex items-center gap-2">Core Platform Feature <ArrowRight size={14}/></div>
            </div>

            {[
              { icon: <BarChart size={24}/>, title: "Detailed report cards", desc: "Skill scores, communication rating, red flags, and a clear hire / no-hire signal. Share directly with your client." },
              { icon: <Smartphone size={24}/>, title: "Mobile & Text First", desc: "Send invites via Email/SMS. Candidates complete interviews on their phones, dramatically increasing completion rates." },
              { icon: <Users size={24}/>, title: "Bulk candidate import", desc: "Upload 500 candidates at once from your ATS or CSV. Invitations and scoring handled automatically in parallel." },
              { icon: <Shield size={24}/>, title: "Proctoring & integrity", desc: "Screen switching detection and AI-generated response flags. Your shortlists are trustworthy." },
              { icon: <Code size={24}/>, title: "Live Code Evaluation", desc: "Built-in IDE and compiler. The AI watches tech candidates code in real-time, testing logic and syntax." },
              { icon: <Palette size={24}/>, title: "White-label branding", desc: "Candidates see your logo, your colours, your name. Impress clients with a branded agency experience." },
            ].map((feat, i) => (
              <div key={i} className="p-8 rounded-[2rem] bg-white/60 dark:bg-[#0a0a0a]/60 backdrop-blur-md border border-slate-200 dark:border-white/10 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300 group">
                <div className="w-12 h-12 bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 rounded-xl flex items-center justify-center mb-6 group-hover:bg-indigo-50 dark:group-hover:bg-indigo-500/20 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">{feat.icon}</div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-3">{feat.title}</h3>
                <p className="text-[15px] font-normal text-slate-600 dark:text-slate-400 leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. METRICS */}
      <section className="py-24 px-6 relative z-10 overflow-hidden">
        <div className="absolute inset-0 bg-indigo-600/5 dark:bg-indigo-500/5"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-12 tracking-tight text-center">What agencies see in the <span className="font-light italic text-indigo-600 dark:text-indigo-400">first 30 days.</span></h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 dark:bg-white/10 border border-slate-200 dark:border-white/10 rounded-[2rem] overflow-hidden">
            {[
              { num: "80%", label: "Reduction in recruiter screening time" },
              { num: "3x", label: "Faster shortlisting to clients" },
              { num: "500+", label: "Candidates screened per month" },
              { num: "90%", label: "Lower cost per screened candidate" }
            ].map((stat, i) => (
              <div key={i} className="bg-white dark:bg-[#0a0a0a] p-8 text-center hover:bg-slate-50 dark:hover:bg-[#111] transition-colors">
                <div className="text-4xl md:text-5xl font-bold text-indigo-600 dark:text-indigo-400 mb-3">{stat.num}</div>
                <div className="text-xs font-bold tracking-wide text-slate-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. PRICING */}
      <section id="pricing" className="py-24 px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="mb-16 text-center flex flex-col items-center">
            <span className="text-[12px] font-bold tracking-wide text-indigo-600 dark:text-indigo-400 flex items-center gap-2 mb-3">
              <div className="w-4 h-0.5 bg-indigo-600 dark:bg-indigo-400"></div> PRICING
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6 tracking-tight">
              Simple pricing.<br /><span className="font-light italic text-indigo-600 dark:text-indigo-400">Top up anytime.</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 font-light max-w-xl">
              Start free with 10 interviews. Buy more whenever you need them — interviews never expire and stack with every purchase.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">

            {/* Free / Starter */}
            <div className="bg-white/60 dark:bg-[#0a0a0a]/60 backdrop-blur-md border border-slate-200 dark:border-white/10 rounded-[2rem] p-8 shadow-sm">
              <div className="text-[12px] font-bold tracking-wide text-slate-500 mb-2">Free to Start</div>
              <div className="flex items-end gap-1 mb-1">
                <span className="text-5xl font-bold text-slate-900 dark:text-white tracking-tight">₹0</span>
              </div>
              <p className="text-[12px] text-slate-400 tracking-wide font-bold mb-4">No credit card needed</p>
              <p className="text-[15px] font-normal text-slate-600 dark:text-slate-400 mb-6 pb-6 border-b border-slate-200 dark:border-white/10">
                Sign up and get 10 free interviews instantly. No commitment.
              </p>

              <div className="bg-slate-50 dark:bg-white/5 rounded-xl px-4 py-4 mb-6 text-center">
                <span className="text-3xl font-bold text-slate-900 dark:text-white">10</span>
                <span className="text-xs font-bold tracking-wide text-slate-500 ml-2">free interviews</span>
              </div>

              <ul className="space-y-3 mb-8">
                {[
                  "10 AI interviews included",
                  "All interview types",
                  "Scored report cards",
                  "Email invites",
                  "Dashboard analytics",
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm font-light text-slate-700 dark:text-slate-300">
                    <div className="w-5 h-5 rounded-full bg-slate-100 dark:bg-white/5 text-slate-500 flex items-center justify-center shrink-0">
                      <Check size={12} />
                    </div>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/register')}
                className="w-full py-3.5 bg-white dark:bg-[#111] border border-slate-200 dark:border-white/10 hover:border-indigo-500 text-slate-900 dark:text-white rounded-xl text-xs font-bold tracking-wide transition-all"
              >
                Get Started Free
              </button>
            </div>

            {/* +30 interviews */}
            <div className="bg-gradient-to-b from-indigo-900/10 to-transparent dark:from-indigo-500/10 border-2 border-indigo-200 dark:border-indigo-500/30 rounded-[2rem] p-8 relative shadow-2xl md:-mt-4">
              <div className="absolute top-5 right-5 bg-indigo-600 text-white text-[12px] font-bold tracking-wide px-3 py-1 rounded-full">
                Popular
              </div>
              <div className="text-[12px] font-bold tracking-wide text-indigo-600 dark:text-indigo-400 mb-2">Top Up</div>
              <div className="flex items-end gap-1 mb-1">
                <span className="text-5xl font-bold text-slate-900 dark:text-white tracking-tight">₹150</span>
                <span className="text-sm font-light text-slate-500 mb-1">/ interview</span>
              </div>
              <p className="text-[12px] text-slate-400 tracking-wide font-bold mb-4">
                ₹4,500 total · +30 interviews
              </p>
              <p className="text-[15px] font-normal text-slate-600 dark:text-slate-400 mb-6 pb-6 border-b border-slate-200 dark:border-white/10">
                Add 30 interviews to your balance. Stack with any future purchase.
              </p>

              <div className="bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 rounded-xl px-4 py-4 mb-6 text-center">
                <span className="text-3xl font-bold text-indigo-700 dark:text-indigo-300">+30</span>
                <span className="text-xs font-bold tracking-wide text-indigo-500 ml-2">interviews added</span>
              </div>

              <ul className="space-y-3 mb-8">
                {[
                  "30 interviews added instantly",
                  "Never expire",
                  "All interview types",
                  "Priority email support",
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm font-light text-slate-700 dark:text-slate-300">
                    <div className="w-5 h-5 rounded-full bg-indigo-50 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                      <Check size={12} />
                    </div>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/register?plan=growth')}
                className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold tracking-wide transition-all shadow-[0_0_20px_rgba(99,102,241,0.4)]"
              >
                Buy +30 Interviews
              </button>
            </div>

            {/* +60 interviews */}
            <div className="bg-white/60 dark:bg-[#0a0a0a]/60 backdrop-blur-md border border-slate-200 dark:border-white/10 rounded-[2rem] p-8 shadow-sm">
              <div className="text-[12px] font-bold tracking-wide text-slate-500 mb-2">Best Value</div>
              <div className="flex items-end gap-1 mb-1">
                <span className="text-5xl font-bold text-slate-900 dark:text-white tracking-tight">₹120</span>
                <span className="text-sm font-light text-slate-500 mb-1">/ interview</span>
              </div>
              <p className="text-[12px] text-slate-400 tracking-wide font-bold mb-4">
                ₹7,200 total · +60 interviews
              </p>
              <p className="text-[15px] font-normal text-slate-600 dark:text-slate-400 mb-6 pb-6 border-b border-slate-200 dark:border-white/10">
                Best rate per interview. Ideal for high-volume agencies.
              </p>

              <div className="bg-slate-50 dark:bg-white/5 rounded-xl px-4 py-4 mb-6 text-center relative">
                <span className="text-3xl font-bold text-slate-900 dark:text-white">+60</span>
                <span className="text-xs font-bold tracking-wide text-slate-500 ml-2">interviews added</span>
                <span className="absolute -top-2 -right-2 bg-emerald-500 text-white text-[12px] font-bold tracking-wide px-2 py-0.5 rounded-full">
                  Save 20%
                </span>
              </div>

              <ul className="space-y-3 mb-8">
                {[
                  "60 interviews added instantly",
                  "Never expire",
                  "All interview types",
                  "Dedicated account manager",
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm font-light text-slate-700 dark:text-slate-300">
                    <div className="w-5 h-5 rounded-full bg-slate-100 dark:bg-white/5 text-slate-500 flex items-center justify-center shrink-0">
                      <Check size={12} />
                    </div>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/register?plan=scale')}
                className="w-full py-4 bg-slate-900 dark:bg-white hover:bg-slate-800 dark:hover:bg-slate-200 text-white dark:text-slate-900 rounded-xl text-xs font-bold tracking-wide transition-all"
              >
                Buy +60 Interviews
              </button>
            </div>

          </div>

          <p className="text-center text-xs text-slate-400 mt-8 tracking-wide">
            One-time purchases · Interviews never expire · Balances stack · GST applicable at checkout
          </p>
        </div>
      </section>
      
      {/* 8. TESTIMONIALS */}
      <section className="py-24 px-6 bg-slate-100/50 dark:bg-[#0a0a0a]/30 relative z-10 border-y border-slate-200/50 dark:border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 flex flex-col items-center text-center">
            <span className="text-[12px] font-bold tracking-wide text-indigo-600 dark:text-indigo-400 flex items-center gap-2 mb-3"><div className="w-4 h-0.5 bg-indigo-600 dark:bg-indigo-400"></div> REVIEWS</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6 tracking-tight">Recruiters who switched<br/>don't <span className="font-light italic text-indigo-600 dark:text-indigo-400">go back.</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { quote: "We screened 120 candidates for a client in one day. Previously that would've taken my team a full week of calls. The report cards were better than our own notes.", name: "Rohan Sharma", role: "Director, Staffing Agency", initials: "RS", color: "indigo" },
              { quote: "Our recruiters were skeptical at first. Now they refuse to do phone screens manually. Sending the link gets 70%+ completion — better than our scheduling ever did.", name: "Neha Patel", role: "MD, IT Recruitment Firm", initials: "NP", color: "violet" },
              { quote: "I sent a client 8 shortlisted candidates with full report cards. They called me the same day to say it was the best submission they'd ever received from any agency.", name: "Anjali Mishra", role: "Founder, RPO Firm", initials: "AM", color: "emerald" }
            ].map((t, i) => (
              <div key={i} className="bg-white dark:bg-[#111] border border-slate-200 dark:border-white/5 p-8 rounded-[2rem] shadow-sm relative">
                <div className="absolute top-6 right-8 text-6xl font-serif text-slate-100 dark:text-white/5 leading-none">"</div>
                <div className="flex gap-1 mb-6 text-amber-400">
                  <Star size={14} fill="currentColor"/> <Star size={14} fill="currentColor"/> <Star size={14} fill="currentColor"/> <Star size={14} fill="currentColor"/> <Star size={14} fill="currentColor"/>
                </div>
                <p className="text-base font-light text-slate-700 dark:text-slate-300 mb-8 leading-relaxed relative z-10">{t.quote}</p>
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm bg-${t.color}-50 dark:bg-${t.color}-500/10 text-${t.color}-600 dark:text-${t.color}-400`}>{t.initials}</div>
                  <div>
                    <div className="font-bold text-sm text-slate-900 dark:text-white">{t.name}</div>
                    <div className="text-[12px] tracking-wide text-slate-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="py-24 px-6 relative z-10">
        <div className="max-w-3xl mx-auto">
          <div className="mb-12 text-center flex flex-col items-center">
            <span className="text-[12px] font-bold tracking-wide text-indigo-600 dark:text-indigo-400 flex items-center gap-2 mb-3"><div className="w-4 h-0.5 bg-indigo-600 dark:bg-indigo-400"></div> FAQ</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white tracking-tight">Questions from agencies <span className="font-light italic text-indigo-600 dark:text-indigo-400">like yours.</span></h2>
          </div>
          
          <div className="border-t border-slate-200 dark:border-white/10">
            {FAQS.map((faq, i) => (
              <FaqItem
                key={i}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 10. CTA SECTION */}
      <section className="py-32 px-6 relative z-10 bg-indigo-600 dark:bg-indigo-900/40 text-center overflow-hidden border-t border-indigo-700 dark:border-white/10">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-white/5 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="max-w-3xl mx-auto relative z-10">
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">Ready to fill roles faster<br/>and <span className="font-light italic text-indigo-200">bill clients sooner?</span></h2>
          <p className="text-lg text-indigo-100 font-light mb-10 max-w-xl mx-auto">Start your free 14-day trial. No credit card required. Your first role live within the hour.</p>
          <div className="flex justify-center gap-4">
            <button onClick={() => navigate('/register')} className="px-8 py-4 bg-white text-indigo-600 rounded-xl text-xs font-bold tracking-wide transition-all hover:bg-indigo-50 shadow-xl active:scale-95 flex items-center justify-center gap-2">
              Start Free Trial <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* 11. FOOTER */}
      <footer className="bg-slate-900 dark:bg-[#05050A] pt-20 pb-10 px-6 border-t border-slate-800 dark:border-white/5 relative z-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-indigo-500">
                <Briefcase size={14} className="text-white" strokeWidth={2} />
              </div>
              <span className="font-bold text-lg tracking-[0.1em] text-white">RecruitGuru</span>
            </div>
            <p className="text-sm font-light text-slate-400 leading-relaxed">
              AI interviews for staffing and recruitment agencies. Screen faster, place sooner, grow your agency.
            </p>
          </div>
          
          <div>
            <h4 className="text-[12px] font-bold tracking-wide text-slate-500 mb-6">Product</h4>
            <div className="flex flex-col gap-4 text-sm font-light text-slate-300">
              <a href="#how" className="hover:text-indigo-400 transition-colors">How it works</a>
              <a href="#features" className="hover:text-indigo-400 transition-colors">Features</a>
              <a href="#pricing" className="hover:text-indigo-400 transition-colors">Pricing</a>
              <Link to="/register" className="hover:text-indigo-400 transition-colors">Start Free Trial</Link>
            </div>
          </div>

          <div>
            <h4 className="text-[12px] font-bold tracking-wide text-slate-500 mb-6">Company</h4>
            <div className="flex flex-col gap-4 text-sm font-light text-slate-300">
              <Link to="/blogs" className="hover:text-indigo-400 transition-colors">Blog & Insights</Link>
              <Link to="/contact" className="hover:text-indigo-400 transition-colors">Contact Sales</Link>
              <Link to="/login" className="hover:text-indigo-400 transition-colors">Recruiter Login</Link>
              <Link to="/candidate-login" className="hover:text-indigo-400 transition-colors">Candidate Portal</Link>
            </div>
          </div>

          <div>
            <h4 className="text-[12px] font-bold tracking-wide text-slate-500 mb-6">Legal</h4>
            <div className="flex flex-col gap-4 text-sm font-light text-slate-300">
              <a href="#" className="hover:text-indigo-400 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-indigo-400 transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-indigo-400 transition-colors">Data Compliance</a>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto pt-8 border-t border-slate-800 dark:border-white/5 flex flex-col md:flex-row items-center justify-between gap-4 text-xs font-light text-slate-500">
          <div>© 2026 RecruitGuru. All rights reserved.</div>
          <div className="flex items-center gap-2">Built with ❤️ for Technical Recruiters</div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;